/**
 * Patch a material to read GPU-computed skinned positions from a storage
 * buffer and skip Three.js's built-in SkinningNode/morphReference.
 *
 * Uses the Three.js-sanctioned .toAttribute() pattern to convert a storage
 * buffer node into a vertex attribute — the same mechanism used by particle
 * systems and compute-driven workflows in Three.js.
 *
 * The storage buffer is NOT registered on the geometry (that would leak to
 * overlay materials). Instead, only the patched material references it.
 */

import { positionLocal, subBuild, vec3 } from "three/tsl";
import type * as THREE from "three/webgpu";
import type { GpuNormalCompute } from "./gpuNormalCompute.ts";

type ShadowBeforeArgs = Parameters<THREE.Object3D["onBeforeShadow"]>;
type ShadowAfterArgs = Parameters<THREE.Object3D["onAfterShadow"]>;
type GpuPositionMaterial = THREE.Material & {
  positionNode?: unknown;
  setupPosition?: unknown;
  customProgramCacheKey?: () => string;
};

const GPU_POSITION_SHADOW_HOOK = Symbol("poserefGpuPositionShadowHook");

function firstMaterial(
  material: THREE.Material | THREE.Material[] | null | undefined,
): THREE.Material | null {
  if (Array.isArray(material)) return material[0] ?? null;
  return material ?? null;
}

/**
 * Patch a material to read GPU-computed skinned positions from the compute
 * pipeline's positionBuffer, and skip Three.js's built-in SkinningNode.
 *
 * @param mat         The material to patch (must be a NodeMaterial).
 * @param gpuNormal   The GPU compute pipeline owning the position storage buffer.
 */
export function patchPositionForGpuSkinning(
  mat: THREE.Material,
  gpuNormal: GpuNormalCompute,
): void {
  const m = mat as any;

  // Convert the compute pipeline's position storage buffer to a vertex
  // attribute node. This reads from the same GPU buffer that Pass 0 writes
  // skinned positions to — no copy, no geometry registration needed.
  const posAttr = (gpuNormal.positionBuffer as any).toAttribute();
  m.positionNode = vec3(posAttr.x, posAttr.y, posAttr.z);

  // Override setupPosition to skip SkinningNode and morphReference —
  // our compute pipeline owns all morphing, skinning, and normal computation.
  m.setupPosition = function (_builder: any) {
    if (this.positionNode !== null) {
      positionLocal.assign(
        (subBuild as any)(this.positionNode, "POSITION", "vec3"),
      );
    }
    return positionLocal;
  };
}

/**
 * Keep WebGPU shadow-map rendering on the same GPU-position path as the
 * visible material.
 *
 * Three's WebGPU shadow pass renders through a shared override material. It
 * copies a material's castShadowPositionNode/positionNode onto that override,
 * but not a custom setupPosition method. Materials patched by
 * patchPositionForGpuSkinning need that method so the override material skips
 * Three's built-in skinning/morph path and reads the compute buffer directly.
 */
export function installGpuPositionShadowHook(mesh: THREE.Mesh): void {
  const hookTarget = mesh as THREE.Mesh & {
    [GPU_POSITION_SHADOW_HOOK]?: true;
  };
  if (hookTarget[GPU_POSITION_SHADOW_HOOK]) return;
  hookTarget[GPU_POSITION_SHADOW_HOOK] = true;

  const originalBefore = mesh.onBeforeShadow;
  const originalAfter = mesh.onAfterShadow;
  let patchedDepthMaterial: GpuPositionMaterial | null = null;
  let hadOwnSetupPosition = false;
  let previousSetupPosition: unknown = null;
  let hadOwnCustomProgramCacheKey = false;
  let previousCustomProgramCacheKey: unknown = null;

  mesh.onBeforeShadow = ((...args: ShadowBeforeArgs) => {
    originalBefore.apply(mesh, args);

    const depthMaterial = args[5] as GpuPositionMaterial | undefined;
    const source = firstMaterial(mesh.material) as GpuPositionMaterial | null;

    if (
      !depthMaterial ||
      !source?.positionNode ||
      typeof source.setupPosition !== "function"
    ) {
      return;
    }

    patchedDepthMaterial = depthMaterial;
    hadOwnSetupPosition = Object.hasOwn(depthMaterial, "setupPosition");
    previousSetupPosition = depthMaterial.setupPosition;
    depthMaterial.setupPosition = source.setupPosition;

    const positionAttr = mesh.geometry.getAttribute("position");
    const baseProgramKey =
      typeof depthMaterial.customProgramCacheKey === "function"
        ? depthMaterial.customProgramCacheKey()
        : "";
    const shadowProgramKey = [
      baseProgramKey,
      "gpu-position-shadow",
      source.uuid,
      mesh.geometry.id,
      positionAttr?.count ?? 0,
    ].join(":");

    hadOwnCustomProgramCacheKey = Object.hasOwn(
      depthMaterial,
      "customProgramCacheKey",
    );
    previousCustomProgramCacheKey = depthMaterial.customProgramCacheKey;
    depthMaterial.customProgramCacheKey = () => shadowProgramKey;
    depthMaterial.needsUpdate = true;
  }) as THREE.Object3D["onBeforeShadow"];

  mesh.onAfterShadow = ((...args: ShadowAfterArgs) => {
    if (patchedDepthMaterial) {
      if (hadOwnSetupPosition) {
        patchedDepthMaterial.setupPosition = previousSetupPosition;
      } else {
        delete patchedDepthMaterial.setupPosition;
      }

      if (hadOwnCustomProgramCacheKey) {
        patchedDepthMaterial.customProgramCacheKey =
          previousCustomProgramCacheKey as () => string;
      } else {
        delete (
          patchedDepthMaterial as { customProgramCacheKey?: () => string }
        ).customProgramCacheKey;
      }

      patchedDepthMaterial = null;
      hadOwnSetupPosition = false;
      previousSetupPosition = null;
      hadOwnCustomProgramCacheKey = false;
      previousCustomProgramCacheKey = null;
    }

    originalAfter.apply(mesh, args);
  }) as THREE.Object3D["onAfterShadow"];
}
